import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { benefitBounce } from "@otto-ec/nexus-registry/benefit/bounce";
import { defineEvent } from "@otto-ec/global-resources/nexus";
import { captureErrorWithPayload } from "utils/error";
import { BenefitElement, defineElement, buildSearchParams, buildFetchHeaders } from "../benefit_base";

const ATTRIBUTE_NAMES = {
	VARIATION_ID: "variation-id",
	FEATURE: "feature",
	TEAM: "team",
};

const log = createLogger("tag", "bounce.component");
const BOUNCE_ELEMENT_NAME = "ft9-bounce-v1";

const ERROR_KEY = "ft9-benefit-tag-bounce-not-ok";
const ERROR_LABELS = { service: "tag", module: "bounce", team: "ft9" };

if (!benefitBounce.isReady) {
	benefitBounce.assignNamespace({
		init: defineEvent(),
	});
}

async function callBackend(url: URL, headers: HeadersInit): Promise<string> {
	const urlSearchParams = url.searchParams.toString();

	try {
		const response = await fetchRetry({
			url: url.toString(),
			namespace: "tag",
			fetchOptions: {
				headers,
				priority: "high",
			},
			retryOptions: {
				limit: 2,
				delay: 50,
			},
		});
		if (!response.ok) {
			log.error("tag endpoint returns a non ok response!", response.status);

			captureErrorWithPayload({
				message: "callBackend error",
				type: ERROR_KEY,
				labels: ERROR_LABELS,
				custom: { url_search_params: urlSearchParams, response_type: response.type, tries: 1 },
				payload: response.status,
			});
			return Promise.reject();
		}
		return response.text();
	} catch (error) {
		log.error("tag bounce request returns an error!", error);
		captureErrorWithPayload({
			message: "callBackend error",
			type: ERROR_KEY,
			labels: ERROR_LABELS,
			custom: { url_search_params: urlSearchParams, tries: 3 },
			payload: error,
		});
		return Promise.reject();
	}
}

export class BounceElement extends BenefitElement {
	protected async hydrate(): Promise<void> {
		this.replaceChildren();

		const params = { variationId: this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID) };
		const searchParams = buildSearchParams(params);
		log.info("Calling bounce for %s with %s", this.id || "id-not-set", searchParams.toString());

		const url = new URL(`/benefit-tag/bounce?${searchParams}`, globalThis.location.origin);
		try {
			const headers = buildFetchHeaders(this.getAttribute(ATTRIBUTE_NAMES.FEATURE), this.getAttribute(ATTRIBUTE_NAMES.TEAM));
			this.innerHTML = await callBackend(url, headers);
			benefitBounce.init.emit({}, { retain: true });
		} catch {
			/*                                                                                    */
		}
	}
}

defineElement(BounceElement, BOUNCE_ELEMENT_NAME, "ftnine_emergency_custom_element_disable_bounce");
